import {
	flattenCategoryGroups,
	resolveCollapseCategoriesFromExpandGroups
} from './auditTrailFilterUtils';
import { getGroupedEventDisplayText } from './auditTrailUtils';

export const EVENT_TYPES = {
	System: 'System',
	CrmSync: 'CrmSync',
	Save: 'Save',
	General: 'General',
	Version: 'Version'
};

export const GROUP_GAP_MINUTES = 60;

const containsAny = (text, needles) => {
	if (!text) {
		return false;
	}

	const lower = text.toLowerCase();
	return needles.some((needle) => lower.includes(needle.toLowerCase()));
};

const stripBracketPrefix = (message) => {
	const match = /^\[([^\]]+)\]\s*([\s\S]*)$/.exec(message ?? '');

	if (!match) {
		return message ?? '';
	}

	const remainder = match[2] ?? '';
	return remainder.trim() ? remainder : message ?? '';
};

export const getRowEventCategory = (row) => row?.EventCategory ?? row?.event_category ?? '';

export const getRowEventSource = (row) => row?.EventSource ?? row?.event_source ?? '';

export const getRowEventInfo = (row) => row?.EventInfo ?? row?.event_info ?? '';

const isStoredSystemOrCrmSync = (category) => {
	const normalized = (category ?? '').toLowerCase();
	return normalized === EVENT_TYPES.System.toLowerCase()
		|| normalized === EVENT_TYPES.CrmSync.toLowerCase();
};

export const isGenericQuoteUpdatedMessage = (message) =>
	Boolean(message) && message.trim().toLowerCase() === 'quote updated';

const isGenericQuoteUpdatedNoise = (row) => {
	const category = getRowEventCategory(row);

	if (category.toLowerCase() === EVENT_TYPES.Save.toLowerCase()) {
		return false;
	}

	const message = stripBracketPrefix(
		getRowEventInfo(row) || row?.ContentText || row?.content_text || ''
	);

	if (!isGenericQuoteUpdatedMessage(message)) {
		return false;
	}

	return category.toLowerCase() !== EVENT_TYPES.Save.toLowerCase();
};

const isMiscNoiseEventInfo = (eventInfo) => containsAny(eventInfo, [
	'opened the quote',
	'multiple quote users warning',
	'multiple users on quote'
]);

/** Rows hidden when show-low-priority-events is off. Expects API-enriched rows. */
export const isHiddenBySystemToggle = (row) => {
	if (!row) {
		return false;
	}

	if (isStoredSystemOrCrmSync(getRowEventCategory(row))) {
		return true;
	}

	if (isGenericQuoteUpdatedNoise(row)) {
		return true;
	}

	return isMiscNoiseEventInfo(getRowEventInfo(row));
};

const isGeneralBucketCategory = (category) => {
	const normalized = (category ?? '').toLowerCase();
	return normalized === EVENT_TYPES.General.toLowerCase()
		|| normalized === EVENT_TYPES.System.toLowerCase();
};

const matchesAnyCategory = (row, requestedCategories) => {
	const category = getRowEventCategory(row);

	return requestedCategories.some((requested) => {
		if (requested.toLowerCase() === EVENT_TYPES.General.toLowerCase()) {
			return isGeneralBucketCategory(category);
		}

		return category.toLowerCase() === requested.toLowerCase();
	});
};

export const applyCategoryFilter = (rows, eventCategories) => {
	if (!rows?.length) {
		return [];
	}

	const requested = (eventCategories ?? []).filter((value) => value?.trim());

	if (!requested.length) {
		return [...rows];
	}

	return rows.filter((row) => matchesAnyCategory(row, requested));
};

export const applySourceFilter = (rows, eventSources) => {
	if (!rows?.length) {
		return [];
	}

	const requested = (eventSources ?? []).filter((value) => value?.trim());

	if (!requested.length) {
		return [...rows];
	}

	const requestedSet = new Set(requested.map((value) => value.toLowerCase()));

	return rows.filter((row) => requestedSet.has(getRowEventSource(row).toLowerCase()));
};

export const applySystemToggleFilter = (rows, includeSystem) => {
	if (!rows?.length) {
		return [];
	}

	if (includeSystem) {
		return [...rows];
	}

	return rows.filter((row) => !isHiddenBySystemToggle(row));
};

const getActorKey = (row) => row?.ImpersonatedByUsername || row?.UserId || '';

const shouldCollapse = (row, collapseCategorySet) => {
	const category = getRowEventCategory(row);
	return Boolean(category) && collapseCategorySet.has(category);
};

const hasSameGroupKey = (anchor, candidate) =>
	getActorKey(anchor).toLowerCase() === getActorKey(candidate).toLowerCase()
	&& anchor.QuoteVersion === candidate.QuoteVersion
	&& getRowEventCategory(anchor).toLowerCase() === getRowEventCategory(candidate).toLowerCase();

const parseEventDateMs = (row) => {
	const raw = row?.EventDate ?? row?.event_date;

	if (!raw) {
		return null;
	}

	const parsed = new Date(raw).getTime();
	return Number.isNaN(parsed) ? null : parsed;
};

const isWithinGap = (newerMs, olderMs) => {
	if (newerMs == null || olderMs == null) {
		return true;
	}

	return ((newerMs - olderMs) / 60000) <= GROUP_GAP_MINUTES;
};

const buildGroupedRow = (children) => {
	const anchor = children[0];
	const groupedRow = {
		...anchor,
		IdQuoteAuditTrail: `group-${anchor.IdQuoteAuditTrail}`,
		IsGrouped: true,
		GroupedCount: children.length,
		GroupedEvents: [...children]
	};

	groupedRow.EventInfo = getGroupedEventDisplayText(groupedRow);
	return groupedRow;
};

export const groupAuditTrailEvents = (rows, collapseCategories) => {
	if (!rows?.length) {
		return [];
	}

	const collapseSet = new Set((collapseCategories ?? []).filter(Boolean));

	if (!collapseSet.size) {
		return rows.map((row) => ({ ...row, IsGrouped: false }));
	}

	const displayItems = [];
	let index = 0;

	while (index < rows.length) {
		const row = rows[index];

		if (!shouldCollapse(row, collapseSet)) {
			displayItems.push({ ...row, IsGrouped: false });
			index += 1;
			continue;
		}

		const children = [row];
		let nextIndex = index + 1;

		while (nextIndex < rows.length) {
			const nextRow = rows[nextIndex];

			if (!shouldCollapse(nextRow, collapseSet) || !hasSameGroupKey(row, nextRow)) {
				break;
			}

			const newerMs = parseEventDateMs(children[children.length - 1]);
			const olderMs = parseEventDateMs(nextRow);

			if (!isWithinGap(newerMs, olderMs)) {
				break;
			}

			children.push(nextRow);
			nextIndex += 1;
		}

		if (children.length === 1) {
			displayItems.push({ ...children[0], IsGrouped: false });
		} else {
			displayItems.push(buildGroupedRow(children));
		}

		index = nextIndex;
	}

	return displayItems;
};

export const processAuditTrailRows = (rows, filters = {}) => {
	const eventCategories = flattenCategoryGroups(filters.categoryGroups);
	const collapseCategories = resolveCollapseCategoriesFromExpandGroups(filters.expandGroups);

	let visible = applyCategoryFilter(rows, eventCategories);
	visible = applySourceFilter(visible, filters.sources);
	visible = applySystemToggleFilter(visible, filters.includeSystem);
	return groupAuditTrailEvents(visible, collapseCategories);
};
